import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, IconButton, Snackbar, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BuffOrDebuff, TreasureItem, WarbandMemberDTO } from '../../types';
import { PopoverItem } from '../../hooks/useItemPopover';
import { getBuffName } from '../../utils/formatting';
import { ItemDetailsPopover } from './ItemDetailsPopover';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ManageMemberItemsModal } from '../modals/ManageMemberItemsModal';
import { UseScrollModal } from '../modals/UseScrollModal';
import {
  clearItemRemainingUses,
  getItemRemainingUses,
  setItemRemainingUses,
} from '../../services/localStorageService';
import { editButtonOffsetSx } from '../../App';

interface MemberMagicItemsProps {
  member: WarbandMemberDTO;
  memberRole: 'wizard' | 'apprentice' | 'soldier';
  editMode: boolean;
  isDefeated: boolean;
  minified?: boolean;
  onItemClick: (event: React.MouseEvent<HTMLElement>, item: PopoverItem) => void;
  allBuffs: BuffOrDebuff[];
  warbandBuffs: BuffOrDebuff[];
  onUpdateMemberBuffs: (buffIds: string[]) => void;
  onUpdateWarbandBuffs: (buffIds: string[]) => void;
  onUpdateTreasureItems: (itemIds: string[]) => void;
}

export const MemberMagicItems: React.FC<MemberMagicItemsProps> = ({
  member,
  memberRole,
  editMode,
  isDefeated,
  minified,
  onItemClick,
  allBuffs,
  warbandBuffs,
  onUpdateMemberBuffs,
  onUpdateWarbandBuffs,
  onUpdateTreasureItems,
}) => {
  const { warbandId, id: memberId } = member;
  const treasureItems = useMemo(
    () => [...(member.treasureItems ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [member.treasureItems],
  );

  const [isManageItemsOpen, setIsManageItemsOpen] = useState(false);
  const [itemPendingDelete, setItemPendingDelete] = useState<TreasureItem | null>(null);
  const [treasureItemPopover, setTreasureItemPopover] = useState<{ anchor: HTMLElement; item: TreasureItem } | null>(null);
  const [pendingUseItem, setPendingUseItem] = useState<TreasureItem | null>(null);
  const [opponentNotice, setOpponentNotice] = useState<string | null>(null);
  const [usesVersion, setUsesVersion] = useState(0);

  const itemRemainingUsesMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of treasureItems) {
      if (item.usesPerGame > 0) {
        map[item.id] = getItemRemainingUses(warbandId, memberId, item);
      }
    }
    return map;
    // usesVersion is intentionally included to force a recompute after each use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warbandId, memberId, treasureItems, usesVersion]);

  const getApplicableBuff = (item: TreasureItem): BuffOrDebuff | null => {
    if (item.buffId) return allBuffs.find((b) => b.id === item.buffId) ?? null;
    if (item.invokedSpellId) return allBuffs.find((b) => b.sourceId === item.invokedSpellId) ?? null;
    return null;
  };

  const getUseItemMessage = (): string => {
    if (!pendingUseItem) return '';
    const buff = getApplicableBuff(pendingUseItem);
    if (!buff) return 'No automated effect — apply the effect manually at the table.';
    const buffName = getBuffName(buff);
    const isGlobalYou = buff.globalYou > 0;
    const isGlobalThem = buff.globalThem > 0;
    if (isGlobalYou && isGlobalThem) {
      return `This will apply ${buffName} to your warband. Your opponent must also apply ${buffName} to their warband.`;
    }
    if (isGlobalYou) return `This will apply ${buffName} to your warband.`;
    if (isGlobalThem) return `This has no automated effect — remind your opponent to apply ${buffName} to their warband.`;
    return `This will apply the effect: ${buffName}`;
  };

  const handleConfirmUse = () => {
    if (!pendingUseItem) return;
    const item = pendingUseItem;
    const remaining = getItemRemainingUses(warbandId, memberId, item);
    const newRemaining = remaining - 1;

    const buff = getApplicableBuff(item);
    if (buff) {
      const isGlobalYou = buff.globalYou > 0;
      const isGlobalThem = buff.globalThem > 0;
      if (isGlobalYou) {
        const existingWarbandIds = warbandBuffs.flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
        onUpdateWarbandBuffs([...existingWarbandIds, buff.id]);
      }
      if (isGlobalThem) {
        setOpponentNotice(getBuffName(buff));
      }
      if (!isGlobalYou && !isGlobalThem) {
        const existingIds = (member.buffsAndDebuffs ?? []).flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
        onUpdateMemberBuffs([...existingIds, buff.id]);
      }
    }

    if (newRemaining <= 0 && !item.refreshAfterGame) {
      const remainingItems = (member.treasureItems ?? [])
        .filter((t) => t.id !== item.id)
        .map((t) => t.id);
      onUpdateTreasureItems(remainingItems);
      clearItemRemainingUses(warbandId, memberId, item.id);
    } else {
      setItemRemainingUses(warbandId, memberId, item.id, newRemaining);
      setUsesVersion((v) => v + 1);
    }

    setPendingUseItem(null);
  };

  const handleConfirmScrollUse = () => {
    if (!pendingUseItem) return;
    const item = pendingUseItem;
    const remaining = getItemRemainingUses(warbandId, memberId, item);
    const newRemaining = remaining - 1;

    if (newRemaining <= 0 && !item.refreshAfterGame) {
      const remainingItems = (member.treasureItems ?? [])
        .filter((t) => t.id !== item.id)
        .map((t) => t.id);
      onUpdateTreasureItems(remainingItems);
      clearItemRemainingUses(warbandId, memberId, item.id);
    } else {
      setItemRemainingUses(warbandId, memberId, item.id, newRemaining);
      setUsesVersion((v) => v + 1);
    }

    setPendingUseItem(null);
  };

  if(minified && treasureItems.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Items
          </Typography>
          {editMode && (
            <IconButton
              sx={editButtonOffsetSx}
              disabled={isDefeated}
              size="small"
              onClick={() => setIsManageItemsOpen(true)}
            >
              <AddIcon />
            </IconButton>
          )}
        </Stack>
      </Box>
      {treasureItems.length ? (
        <Stack direction="column" spacing={1} alignItems="flex-start">
          {treasureItems.map((item) => (
            <Chip
              key={item.id}
              size="small"
              label={
                !editMode && item.usesPerGame > 0
                  ? `${item.name} (${itemRemainingUsesMap[item.id] ?? item.usesPerGame}/${item.usesPerGame})`
                  : item.name
              }
              onClick={(event) => {
                if (!editMode) {
                  setTreasureItemPopover({ anchor: event.currentTarget, item });
                } else {
                  onItemClick(event, { id: item.id, name: item.name, description: item.description, specialTags: [] });
                }
              }}
              onDelete={editMode ? () => setItemPendingDelete(item) : undefined}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="textSecondary">
          None
        </Typography>
      )}

      <ManageMemberItemsModal
        open={isManageItemsOpen}
        warbandId={warbandId}
        member={member}
        onClose={() => setIsManageItemsOpen(false)}
      />

      {/* Treasure item detail popover (play mode) */}
      <ItemDetailsPopover
        popover={
          treasureItemPopover
            ? {
              anchor: treasureItemPopover.anchor,
              name: treasureItemPopover.item.name,
              description: treasureItemPopover.item.description,
              tags: [],
            }
            : null
        }
        onClose={() => setTreasureItemPopover(null)}
        showUseAction={!editMode && (treasureItemPopover?.item.usesPerGame ?? -1) > 0}
        useDisabled={
          treasureItemPopover !== null &&
          treasureItemPopover.item.usesPerGame > 0 &&
          getItemRemainingUses(warbandId, memberId, treasureItemPopover.item) <= 0
        }
        remainingUsesLabel={
          treasureItemPopover && treasureItemPopover.item.usesPerGame > 0
            ? `Uses remaining: ${getItemRemainingUses(warbandId, memberId, treasureItemPopover.item)} / ${treasureItemPopover.item.usesPerGame}`
            : undefined
        }
        onUse={() => {
          if (!treasureItemPopover) return;
          setPendingUseItem(treasureItemPopover.item);
          setTreasureItemPopover(null);
        }}
      />

      {/* Use item confirmation — scroll with invokedSpellId shows spell card modal */}
      {pendingUseItem?.invokedSpellId ? (
        <UseScrollModal
          open={pendingUseItem !== null}
          item={pendingUseItem}
          invokedSpellId={pendingUseItem.invokedSpellId}
          onClose={() => setPendingUseItem(null)}
          onConfirm={handleConfirmScrollUse}
        />
      ) : (
        <ConfirmActionDialog
          open={pendingUseItem !== null}
          title={`Use ${pendingUseItem?.name ?? 'item'}`}
          message={getUseItemMessage()}
          confirmLabel="Use"
          onClose={() => setPendingUseItem(null)}
          onConfirm={handleConfirmUse}
        />
      )}

      {/* Delete item confirmation */}
      <ConfirmActionDialog
        open={itemPendingDelete !== null}
        title="Remove magic item"
        message={itemPendingDelete ? `Remove "${itemPendingDelete.name}" from ${member.name}?` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setItemPendingDelete(null)}
        onConfirm={() => {
          if (!itemPendingDelete) return;
          const remaining = (member.treasureItems ?? [])
            .filter((t) => t.id !== itemPendingDelete.id)
            .map((t) => t.id);
          onUpdateTreasureItems(remaining);
          setItemPendingDelete(null);
        }}
      />

      {/* Opponent notice after using a GlobalThem scroll */}
      <Snackbar
        open={opponentNotice !== null}
        onClose={() => setOpponentNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setOpponentNotice(null)} sx={{ width: '100%' }}>
          Your opponent should apply <strong>{opponentNotice}</strong> to their warband.
        </Alert>
      </Snackbar>
    </Box>
  );
};
